import React from 'react';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
} from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import {
  PolicyHeaderDateFormat,
  PolicyHeaderDateFillOption,
  PolicyHeaderIssuingAuthorityType,
} from '../../../_types/manuals/draft';
import HeaderOrderingSection from './HeaderOrderingSection';
import { useManualPropertiesContext } from '../context';

// Translation key mappings - serves as both config and translation source
const DATE_FILL_OPTIONS: Record<PolicyHeaderDateFillOption, string> = {
  AUTO_FILL_ONLY: 'draftManuals.manualProperties.policyHeaders.autoFillOnly',
  MANUAL_SELECT_ONLY: 'draftManuals.manualProperties.policyHeaders.manualSelectOnly',
};

const ISSUING_AUTHORITY_OPTIONS: Record<PolicyHeaderIssuingAuthorityType, string> = {
  SIGNATURE_AND_NAME: 'draftManuals.manualProperties.policyHeaders.signatureAndName',
  SIGNATURE_ONLY: 'draftManuals.manualProperties.policyHeaders.signatureOnly',
  NAME_ONLY: 'draftManuals.manualProperties.policyHeaders.nameOnly',
};

const PolicyHeadersConfiguration: React.FC = () => {
  const {
    headerState,
    setHeaderState,
    dateFormat,
    setDateFormat,
    effectiveDateOption,
    setEffectiveDateOption,
    revisedDateOption,
    setRevisedDateOption,
    issuingAuthorityType,
    setIssuingAuthorityType,
    headerOrder,
  } = useManualPropertiesContext();
  const handleHeaderStateChange = (newState: 'ENABLED' | 'DISABLED') => {
    setHeaderState(newState);
    if (newState === 'DISABLED') {
      setDateFormat('MMDDYYYY');
      setEffectiveDateOption('AUTO_FILL_ONLY');
      setRevisedDateOption('AUTO_FILL_ONLY');
      setIssuingAuthorityType('SIGNATURE_AND_NAME');
    }
  };

  const renderDateFillOptions = () =>
    Object.entries(DATE_FILL_OPTIONS).map(([value, translationKey]) => (
      <MenuItem
        key={value}
        value={value}
        sx={{
          py: 1.5,
          px: 2,
          fontWeight: 400,
        }}
      >
        {t(translationKey)}
      </MenuItem>
    ));

  const renderIssuingAuthorityOptions = () =>
    Object.entries(ISSUING_AUTHORITY_OPTIONS).map(([value, translationKey]) => (
      <MenuItem
        key={value}
        value={value}
        sx={{
          py: 1.5,
          px: 2,
          fontWeight: 400,
        }}
      >
        {t(translationKey)}
      </MenuItem>
    ));

  return (
    <Box>
      <FormControl sx={{ mb: 3 }}>
        <FormLabel>{t('draftManuals.manualProperties.policyHeaders.includeHeaders')}</FormLabel>
        <RadioGroup
          row
          value={headerState}
          onChange={(e) => handleHeaderStateChange(e.target.value as 'ENABLED' | 'DISABLED')}
        >
          <FormControlLabel
            value='ENABLED'
            control={<Radio />}
            label={t('draftManuals.manualProperties.policyHeaders.yes')}
          />
          <FormControlLabel
            value='DISABLED'
            control={<Radio />}
            label={t('draftManuals.manualProperties.policyHeaders.no')}
          />
        </RadioGroup>
      </FormControl>

      {headerState === 'ENABLED' && (
        <Stack spacing={3}>
          <FormControl>
            <FormLabel>{t('draftManuals.manualProperties.policyHeaders.dateFormat')}</FormLabel>
            <RadioGroup
              row
              value={dateFormat}
              onChange={(e) => setDateFormat(e.target.value as PolicyHeaderDateFormat)}
            >
              <FormControlLabel
                value='MMDDYYYY'
                control={<Radio />}
                label={t('draftManuals.manualProperties.policyHeaders.dateFormatShort')}
              />
              <FormControlLabel
                value='MMYYYY'
                control={<Radio />}
                label={t('draftManuals.manualProperties.policyHeaders.dateFormatLong')}
              />
            </RadioGroup>
          </FormControl>

          <FormControl fullWidth>
            <FormLabel sx={{ mb: 1 }}>
              {t('draftManuals.manualProperties.policyHeaders.effectiveDateOptions')}
            </FormLabel>
            <Select
              name="effectiveDateOption"
              value={effectiveDateOption}
              onChange={(e) => setEffectiveDateOption(e.target.value as PolicyHeaderDateFillOption)}
              MenuProps={{
                PaperProps: {
                  sx: {
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                    borderRadius: 2,
                    mt: 1,
                  },
                },
              }}
            >
              {renderDateFillOptions()}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <FormLabel sx={{ mb: 1 }}>{t('draftManuals.manualProperties.policyHeaders.revisedDateOptions')}</FormLabel>
            <Select
              name="revisedDateOption"
              value={revisedDateOption}
              onChange={(e) => setRevisedDateOption(e.target.value as PolicyHeaderDateFillOption)}
              MenuProps={{
                PaperProps: {
                  sx: {
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                    borderRadius: 2,
                    mt: 1,
                  },
                },
              }}
            >
              {renderDateFillOptions()}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <FormLabel sx={{ mb: 1 }}>
              {t('draftManuals.manualProperties.policyHeaders.issuingAuthorityOptions')}
            </FormLabel>
            <Select
              value={issuingAuthorityType}
              onChange={(e) => setIssuingAuthorityType(e.target.value as PolicyHeaderIssuingAuthorityType)}
              MenuProps={{
                PaperProps: {
                  sx: {
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                    borderRadius: 2,
                    mt: 1,
                  },
                },
              }}
            >
              {renderIssuingAuthorityOptions()}
            </Select>
          </FormControl>

          {/* Header Ordering and Visibility Section */}
          {headerOrder.length > 0 && <HeaderOrderingSection />}
        </Stack>
      )}
    </Box>
  );
};

export default PolicyHeadersConfiguration;